import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ScreenHeader } from '../components/ScreenHeader';
import { colors, screenPadding, spacing, typography } from '../theme/tokens';
import { useGabarit } from '../hooks/useGabarit';
import { t } from '../i18n';
import { ouvrirLien } from '../utils/ouvrirLien';

const CONTACT_EMAIL = 'info@alternjob.com';

// Lien mailto cliquable (Text imbriqué dans Text, autorisé en RN).
function Mail({ testID }: { testID?: string }) {
  return (
    <Text testID={testID} style={styles.link} onPress={() => ouvrirLien(`mailto:${CONTACT_EMAIL}`)}>
      {CONTACT_EMAIL}
    </Text>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <Text style={styles.paragraph}>{children}</Text>;
}

function Bullets({ items }: { items: string[] }) {
  return (
    <View style={styles.bullets}>
      {items.map((it, i) => (
        <View key={i} style={styles.bulletRow}>
          <Text style={styles.bulletDot}>•</Text>
          <Text style={styles.bulletText}>{it}</Text>
        </View>
      ))}
    </View>
  );
}

// Suppression de compte et de données v1.0 (texte validé). Exigé par Google Play pour la fiche
// Play Store et le formulaire Data Safety. Publié depuis cet écran par scripts/buildLegalDocs.js :
// c'est ce fichier qui fait foi, jamais une copie externe.
export function DataDeletionScreen({ onBack }: { onBack?: () => void }) {
  const { large } = useGabarit();
  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('legalHeaders.dataDeletion')} testID="data-deletion-back" />
      <ScrollView contentContainerStyle={[styles.content, large && styles.contentL]}>
        <Text style={styles.docTitle}>SUPPRESSION DE COMPTE ET DE DONNÉES - HORMÉIA</Text>
        <Text style={styles.docSubtitle}>Version 1.3 - En vigueur au 28/10/2026</Text>

        <P>Application : Horméia (com.hormeia.app)</P>
        <P>
          Éditeur : Alternjob, SAS au capital de 1 000 €, RCS Nanterre n° 892 356 965, siège social au
          34 route des Gardes, 92190 Meudon
        </P>
        <P>
          Contact : <Mail testID="data-deletion-email-editeur" />
        </P>

        <Section title="1. Supprimer ton compte depuis l’application">
          <P>
            C’est la méthode la plus rapide, elle prend moins d’une minute et ne nécessite aucune démarche
            de notre part.
          </P>
          <Bullets
            items={[
              'Étape 1 : ouvre l’application Horméia et connecte-toi.',
              'Étape 2 : va dans l’onglet Profil.',
              'Étape 3 : ouvre Paramètres.',
              'Étape 4 : descends jusqu’à la section « Zone de danger ».',
              'Étape 5 : appuie sur « Supprimer mon compte ».',
              'Étape 6 : confirme en appuyant sur « Supprimer » dans la fenêtre d’avertissement.',
            ]}
          />
          <P>
            Ton compte est désactivé immédiatement : tu es déconnecté et renvoyé vers l’écran de connexion.
            Une copie de tes données de compte est conservée 29 jours dans un espace isolé, pour pouvoir le
            restaurer sur demande ; elle est ensuite supprimée définitivement.
          </P>
          <P>
            La suppression ne résilie pas automatiquement ton abonnement : résilie-le dans les réglages de
            l’App Store ou de Google Play.
          </P>
        </Section>

        <Section title="2. Supprimer ton compte par e-mail">
          <P>
            Si tu n’as plus accès à l’application (téléphone perdu, désinstallation, impossibilité de te
            connecter), envoie une demande à <Mail /> depuis l’adresse e-mail associée à ton compte
            Horméia, avec pour objet « Suppression de compte ».
          </P>
          <P>
            Nous vérifions ton identité, puis nous traitons la demande sous 30 jours maximum, conformément
            au RGPD. Nous te confirmons la suppression par e-mail.
          </P>
        </Section>

        <Section title="3. Données supprimées">
          <P>
            Lorsque ton compte est supprimé, les données suivantes sont effacées de nos serveurs. Ton
            profil, tes CV et ton abonnement restent 29 jours dans la copie de restauration (section 1),
            puis sont supprimés définitivement ; tes photos et ta connexion LinkedIn sont effacées
            immédiatement :
          </P>
          <Bullets
            items={[
              'ton compte d’authentification (adresse e-mail, mot de passe chiffré, identifiants de connexion Google ou Apple) ;',
              'ton profil (nom, prénom, adresse e-mail, numéro de téléphone, adresse postale, date de naissance, ville, niveau d’études, disponibilité, objectif de recherche, URL LinkedIn) ;',
              'tous tes CV, leurs versions successives et leurs scores ATS ;',
              'tes photos (profil et CV) stockées dans l’application ;',
              'tes compteurs d’utilisation et l’historique de tes sessions ;',
              'tes mesures d’usage de l’Application (événements de parcours, sans contenu), ainsi que les événements en attente d’envoi sur ton appareil ;',
              'la ligne d’abonnement enregistrée dans notre base ; notre prestataire de gestion des abonnements (RevenueCat) conserve sa propre fiche client, selon sa politique ;',
              'ta connexion LinkedIn et le jeton d’accès associé, si tu en avais créé une.',
            ]}
          />
          <P>
            Les demandes de coaching que tu as éventuellement envoyées sont anonymisées : ton nom, ton
            adresse e-mail et le contenu de ton message sont effacés, seule la trace technique non
            identifiante est conservée.
          </P>
        </Section>

        <Section title="4. Données conservées après la suppression">
          <P>
            Certaines données sont conservées au-delà de la suppression du compte, uniquement lorsque la
            loi l’impose ou pour un intérêt légitime strictement encadré :
          </P>
          <Bullets
            items={[
              'les factures et pièces comptables liées à un abonnement payant : 10 ans, obligation légale de conservation comptable (article L123-22 du Code de commerce) ;',
              'les journaux techniques d’erreur anonymisés (type d’erreur, code HTTP, fonctionnalité concernée), qui ne contiennent aucune donnée identifiante : 12 mois maximum ;',
              'les données strictement nécessaires à la constatation, l’exercice ou la défense d’un droit en justice, pour la durée de la prescription applicable.',
            ]}
          />
          <P>
            Aucune de ces données ne permet de te réidentifier, à l’exception des pièces comptables, dont
            la conservation est imposée par la loi.
          </P>
        </Section>

        <Section title="5. Données non conservées">
          <P>
            Les documents que tu envoies pour analyse (CV au format PDF, fiches de poste) sont traités de
            manière éphémère : ils sont transmis au moteur d’analyse, le résultat t’est renvoyé, et le
            fichier n’est jamais stocké sur nos serveurs.
          </P>
          <P>
            La dictée des simulations d’entretien utilise la reconnaissance vocale d’Apple ou de Google,
            selon ton appareil : l’audio peut être traité par leurs serveurs, selon leurs propres
            politiques. Horméia ne reçoit que le texte transcrit et ne conserve aucun fichier audio.
          </P>
        </Section>

        <Section title="6. Supprimer uniquement certaines données">
          <P>
            Tu peux supprimer un CV à tout moment depuis l’onglet « Mes CV » sans supprimer ton compte.
          </P>
          <P>
            Tu peux déconnecter ton compte LinkedIn depuis Profil {'>'} Paramètres {'>'} Confidentialité et
            données. Le jeton d’accès est alors immédiatement supprimé de nos serveurs.
          </P>
        </Section>

        <Section title="7. Nous contacter">
          <P>
            Pour toute question relative à la suppression de tes données, ou pour exercer tes autres droits
            RGPD (accès, rectification, portabilité, opposition), écris à{' '}
            <Mail testID="data-deletion-email-droits" />.
          </P>
          <P>
            Délégué à la protection des données : Julien DAUZATS, CEO - <Mail />
          </P>
          <P>Tu peux également introduire une réclamation auprès de la CNIL : www.cnil.fr</P>
        </Section>

        <Text testID="data-deletion-version" style={styles.version}>
          Version 1.3 - En vigueur au 28/10/2026
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xxl },
  // Écran large : colonne de lecture centrée, pas une ligne de texte de 1200 px.
  contentL: { width: '100%', maxWidth: 820, alignSelf: 'center', padding: spacing.xl, paddingBottom: spacing.xxl },
  docTitle: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  docSubtitle: { fontSize: typography.small.size, color: colors.grey500 },
  section: { gap: spacing.sm },
  sectionTitle: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.grey900 },
  paragraph: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  bullets: { gap: spacing.xs },
  bulletRow: { flexDirection: 'row', gap: spacing.sm },
  bulletDot: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  bulletText: { flex: 1, fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  link: { color: colors.electric, fontWeight: '700' },
  version: { fontSize: typography.small.size, color: colors.grey500, marginTop: spacing.md },
});
